export const EMPLOYER_STATUSES = ['READY', 'DOCS_INCOMPLETE', 'AT_CAPACITY', 'DO_NOT_USE'] as const;

export type EmployerStatus = (typeof EMPLOYER_STATUSES)[number];

export const EMPLOYER_BUSINESS_TYPES = [
  'Childcare / Early Learning',
  'Aged Care',
  'Disability Support',
  'Community Services',
  'Healthcare',
  'Education & Training',
  'Fitness & Sport',
  'Hospitality',
  'Retail',
] as const;

export type EmployerBusinessType = (typeof EMPLOYER_BUSINESS_TYPES)[number];

const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

export function normaliseAbn(value: string): string {
  // Strip whitespace and hyphens so pasted formats like "12 345 678 901" and
  // "12-345-678-901" both match a stored 11-digit ABN.
  return value.replace(/[\s-]/g, '');
}

// Validates an Australian Business Number: 11 digits passing the ABR checksum —
// subtract 1 from the first digit, apply the position weights, and the weighted
// sum must divide evenly by 89. https://abr.business.gov.au/Help/AbnFormat
export function isValidAbn(abn: string): boolean {
  if (!/^\d{11}$/.test(abn)) {
    return false;
  }
  const sum = ABN_WEIGHTS.reduce((total, weight, index) => {
    const digit = Number(abn[index]) - (index === 0 ? 1 : 0);
    return total + digit * weight;
  }, 0);
  return sum % 89 === 0;
}

// Groups a stored (stripped) ABN into the ABR's 2-3-3-3 display format, e.g.
// "51824753556" -> "51 824 753 556". Returns null unchanged and leaves a value
// that isn't 11 digits as-is.
export function formatAbn(abn: string | null): string | null {
  if (abn == null) {
    return null;
  }
  const match = /^(\d{2})(\d{3})(\d{3})(\d{3})$/.exec(abn);
  return match != null ? `${match[1]} ${match[2]} ${match[3]} ${match[4]}` : abn;
}

export function deriveEmployerStatus(employer: {
  excludedAt: Date | null;
  compliant: boolean;
}): EmployerStatus {
  if (employer.excludedAt != null) {
    return 'DO_NOT_USE';
  }
  if (!employer.compliant) {
    return 'DOCS_INCOMPLETE';
  }
  return 'READY';
}
